import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

declare global {
  interface Window {
    DD_LOGS: any;
    __DEVTOOL_REQUEST_ERROR_BUFFER__?: RequestErrorLogEntry[];
  }
}

export enum LOG_TYPE {
  error = "error",
  warn = "warn",
  info = "info",
}

export type RequestFailureCategory =
  | "network"
  | "timeout"
  | "auth"
  | "rate_limit"
  | "server"
  | "client"
  | "backend_failure_payload"
  | "unknown";

export type RequestErrorLogEntry = {
  id: string;
  timestamp: number;
  method?: string;
  url?: string;
  status?: number;
  category: RequestFailureCategory;
  summary?: string;
  source: "response" | "error";
};

export const REQUEST_ERROR_BUFFER_KEY = "__DEVTOOL_REQUEST_ERROR_BUFFER__";

const MAX_REQUEST_ERROR_ENTRIES = 200;

// Please use the logger from "@hopper-b2b/datadog" instead
export const logger = () => {
  return window.DD_LOGS ? window.DD_LOGS.logger : undefined;
};

const isSuccessfulResponse = (responseStatus: number) => {
  return responseStatus >= 200 && responseStatus < 300;
};

const isObjectLike = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const shouldCaptureRequestErrorsInBrowser = () => {
  return (
    typeof window !== "undefined" &&
    (!import.meta.env.PROD || import.meta.env.VITE_DEVTOOL === "true")
  );
};

const pushRequestErrorEntry = (
  entry: Omit<RequestErrorLogEntry, "id" | "timestamp"> & {
    timestamp?: number;
  }
) => {
  if (!shouldCaptureRequestErrorsInBrowser()) {
    return;
  }

  const buffer = (window[REQUEST_ERROR_BUFFER_KEY] ??= []);

  buffer.push({
    id: `${entry.timestamp ?? Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: entry.timestamp ?? Date.now(),
    ...entry,
  });

  if (buffer.length > MAX_REQUEST_ERROR_ENTRIES) {
    buffer.splice(0, buffer.length - MAX_REQUEST_ERROR_ENTRIES);
  }
};

const compactText = (value: unknown, maxLength = 160) => {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.replace(/\s+/g, " ").trim();
  if (!normalized) {
    return undefined;
  }

  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength - 1)}…`
    : normalized;
};

const sanitizeRequestUrl = (url?: string) => {
  if (!url) {
    return undefined;
  }

  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split(/[?#]/, 1)[0];
  }
};

const toDisplayMethod = (method: unknown) => {
  return typeof method === "string" ? method.toUpperCase() : undefined;
};

const summarizeBackendFailurePayload = (data: unknown) => {
  if (!isObjectLike(data)) {
    return undefined;
  }

  const summarizeErrors = () => {
    if (!Array.isArray(data.errors) || data.errors.length === 0) {
      return undefined;
    }

    const firstError = data.errors[0];

    if (typeof firstError === "string") {
      const firstSummary = compactText(firstError);

      return firstSummary
        ? `errors=${data.errors.length} first=${firstSummary}`
        : `errors=${data.errors.length}`;
    }

    if (isObjectLike(firstError)) {
      const firstMessage =
        compactText(firstError.message) ??
        compactText(firstError.error) ??
        compactText(firstError.code);

      return firstMessage
        ? `errors=${data.errors.length} first=${firstMessage}`
        : `errors=${data.errors.length}`;
    }

    return `errors=${data.errors.length}`;
  };

  if (data.Response === "Failure") {
    const errorsSummary = summarizeErrors();

    return errorsSummary
      ? `Response=Failure ${errorsSummary}`
      : "Response=Failure";
  }

  const errorsSummary = summarizeErrors();

  if (errorsSummary) {
    return errorsSummary;
  }

  return undefined;
};

const isTrackingEventRequest = (error: AxiosError) => {
  const url = error.config?.url ?? error.request?.responseURL;

  return typeof url === "string" && url.includes("/tracking/event");
};

const hasBackendFailurePayloadForSharedLogging = (response: AxiosResponse) => {
  return (
    isSuccessfulResponse(response.status) &&
    isObjectLike(response.data) &&
    (response.data.Response === "Failure" ||
      (Array.isArray(response.data.errors) && response.data.errors.length > 0))
  );
};

const hasBackendFailurePayloadForDevtoolBuffer = (response: AxiosResponse) => {
  return (
    isSuccessfulResponse(response.status) &&
    isObjectLike(response.data) &&
    response.data.Response === "Failure"
  );
};

const classifyAxiosError = (error: AxiosError): RequestFailureCategory => {
  const status = error.response?.status;
  const code = error.code;
  const message = error.message.toLowerCase();

  if (code === "ECONNABORTED" || message.includes("timeout")) {
    return "timeout";
  }

  if (!error.response) {
    if (code === "ERR_NETWORK" || message.includes("network error")) {
      return "network";
    }

    return "unknown";
  }

  if (status === 401 || status === 403) {
    return "auth";
  }

  if (status === 429) {
    return "rate_limit";
  }

  if (status && status >= 500) {
    return "server";
  }

  if (status && status >= 400) {
    return "client";
  }

  return "unknown";
};

const buildResponseLogMetadata = (res: AxiosResponse) => {
  const category = hasBackendFailurePayloadForSharedLogging(res)
    ? "backend_failure_payload"
    : undefined;
  const summary = summarizeBackendFailurePayload(res.data);

  return {
    URL: res.config.url,
    method: res.config.method,
    status: res.status,
    ...(category ? { category } : {}),
    ...(category && summary ? { summary } : {}),
  };
};

const buildErrorLogMetadata = (error: AxiosError) => {
  const category = classifyAxiosError(error);

  return {
    URL: error.config?.url,
    method: error.config?.method,
    status: error.response?.status,
    category,
    ...(error.code ? { code: error.code } : {}),
    summary: summarizeBackendFailurePayload(error.response?.data),
  };
};

export const createRequestLog = (req: AxiosRequestConfig) => {
  import.meta.env.PROD &&
    logger() &&
    logger().info(`Request: ${req.method?.toUpperCase()} ${req.url}`, {
      request: {
        URL: req.url,
        method: req.method,
      },
    });
};

export const createResponseLog = (res: AxiosResponse) => {
  const shouldLogAsError = hasBackendFailurePayloadForSharedLogging(res);
  const shouldCaptureInBuffer = hasBackendFailurePayloadForDevtoolBuffer(res);
  const metadata = buildResponseLogMetadata(res);

  if (shouldCaptureInBuffer) {
    pushRequestErrorEntry({
      method: toDisplayMethod(res.config.method),
      url: sanitizeRequestUrl(res.config.url),
      status: res.status,
      category: "backend_failure_payload",
      summary: metadata.summary,
      source: "response",
    });
  }

  import.meta.env.PROD &&
    logger() &&
    logger()[shouldLogAsError ? LOG_TYPE.error : LOG_TYPE.info](
      `Response: ${res.config.method?.toUpperCase()} ${res.config.url} Status: ${res.status}`,
      {
        [shouldLogAsError ? "error" : "response"]: metadata,
      }
    );
};

export const createResponseErrorLog = (error: AxiosError) => {
  if (!error || !error.config) return;

  const metadata = buildErrorLogMetadata(error);

  if (!isTrackingEventRequest(error)) {
    pushRequestErrorEntry({
      method: toDisplayMethod(error.config.method),
      url: sanitizeRequestUrl(error.config.url),
      status: error.response?.status,
      category: metadata.category,
      summary: metadata.summary ?? compactText(error.message),
      source: "error",
    });
  }

  import.meta.env.PROD &&
    logger() &&
    logger().error(
      `Response: ${error.config.method?.toUpperCase()} ${
        error.config.url
      } Status: ${error.response?.status}`,
      {
        error: {
          ...metadata,
        },
      }
    );
};
